<script setup lang="ts">
import { ref, computed, watch, h, onMounted } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { resetTimer } from '@/utils/dateUtils';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import { helpers, required, minValue, requiredIf } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { TOUR_5_TO_10, TOUR_11_TO_16, TOUR_17_OR_MORE } from '@/constants';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useTabComposable } from '@/components/tabs/useTabComposable';
import { useTourManagementStore } from '@/store/TourManagementStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import ModalSelectDate from '@/components/modal/ModalSelectDate.vue';
import ValidateQuoteFormNotification from '@/components/modal/ValidateQuoteFormNotification.vue';

const props = defineProps<{
  tourForm: object;
  tourIndex: number;
  listGateway: Array<any>;
  advancedForm: object[];
  isValidation: boolean;
  isDisplayTour: boolean;
  isLoading: boolean;
  isShowError: boolean;
  firstOption: object;
  calendarData?: object;
}>();

const emits = defineEmits<{
  handleSubmit: [];
  clearTours: [index: any, key: string];
  openAutoSearch: [isEdit: any, data: any];
  'update:tourForm': [value: any];
  'update:isDisplayTour': [value: any];
  'update:isLoading': [value: any];
  'update:isShowError': [value: any];
}>();

const currentLanguage = getCurrentLocale();
const dialog = useDialog();
const toast = useToast();
const getistGateways = ref([]);
const filterGateway = ref<any>([]);
const filterDestination = ref<any>([]);
const disableDestination = ref(true);
const disableDate = ref(true);
const listFullDest = ref<any>([]);
const offset = ref(1);
const limit = 40;
const limitCalendar = ref({});
const totalItem = ref(0);
const isGetTotal = ref(true);
const minDateRangeValue = ref(new Date(DateTime.utc().plus({ days: 60 })));
const maxDateRangeValue = ref<Date | undefined>(new Date(DateTime.utc().plus({ days: 346 })));

const { renderListMonth, formatCountryFromCity } = useComponentComposable();
const { getQuoteDepartureData, getLimitDate, filterDatesInRange } = useTabComposable();
const tourManagementStore = useTourManagementStore();
const viewQuoteStore = useViewQuoteStore();
const listStoreAllTours = computed(() => tourManagementStore.listAllTours);
const isRequote = computed(() => viewQuoteStore.selectIsRequote);
const listDestination = computed(() => tourManagementStore.listDestination);
const isWorksheetRequote = computed(() => viewQuoteStore.isWorksheetRequote);

const { t } = useI18n({ useScope: 'global' });
const monthNames = renderListMonth(currentLanguage);
const listTour = ref([]);
const getCityDestinationDetail = ref<any>(listDestination.value?.length ? listDestination.value : []);
const listAllTours = ref<any>(listStoreAllTours.value && props.tourIndex != 0 ? listStoreAllTours.value : []);
const tourDetail = computed({
  get() {
    return props.tourForm;
  },
  set(value) {
    emits('update:tourForm', value);
  }
});
const tourSearchData = ref([]);

const displayTour = computed({
  get() {
    return props.isDisplayTour;
  },
  set(value) {
    emits('update:isDisplayTour', value);
  }
});

const getDate = ref(!tourDetail.value.isDepartureMonth);

const loadingForm = computed({
  get() {
    return props.isLoading;
  },
  set(value) {
    emits('update:isLoading', value);
  }
});

const getMonth = ref(tourDetail.value.isDepartureMonth);

const showError = computed({
  get() {
    return props.isShowError;
  },
  set(value) {
    emits('update:isShowError', value);
  }
});

const isDisabledTourName = computed(() => {
  return tourDetail.value.gateway && tourDetail.value.destination && tourDetail.value.tourDurations && props.advancedForm[0].groupTypeId && (tourDetail.value.departureMonth || tourDetail.value.departureDay);
});

const isDisabledDuration = computed(() => {
  return tourDetail.value.departureMonth || tourDetail.value.departureDay;
});

const listDuration = ref([
  { range: t('management.tour.duration.all'), value: -1 },
  { range: t('management.tour.duration.5_to_10'), value: TOUR_5_TO_10 },
  { range: t('management.tour.duration.11_to_16'), value: TOUR_11_TO_16 },
  { range: t('management.tour.duration.more'), value: TOUR_17_OR_MORE }
]);
const ruleValue = ref<any>({
  gateway: { required: helpers.withMessage(t('validate.required'), required) },
  destination: { required: helpers.withMessage(t('validate.required'), required) },
  tourDurations: { required: helpers.withMessage(t('validate.required'), required) },
  adults: { required: helpers.withMessage(t('validate.required'), required), minValue: helpers.withMessage(t('validate.min_value'), minValue(1)) },
  numOfRooms: {
    required: helpers.withMessage(t('validate.required'), required),
    minValue: helpers.withMessage(t('validate.min_value'), minValue(1))
  },
  departureDay: { requiredIfRef: requiredIf(getDate.value) },
  departureMonth: { requiredIfRef: requiredIf(getMonth.value) },
  tourName: { required: helpers.withMessage(t('validate.required'), required) }
});

const v$: any = useVuelidate(ruleValue.value, tourDetail.value);

const getMonthYearList = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const monthYearList = [];
  for (let i = 0; i < 12; i++) {
    const monthIndex = (currentMonth + i) % 12;
    const year = currentYear + Math.floor((currentMonth + i) / 12);
    const formattedMonthYear = `${monthNames[monthIndex]} ${year}`;
    const dateValue = (monthIndex + 1).toString().padStart(2, '0');
    monthYearList.push({ label: formattedMonthYear, value: `${year}-${dateValue}-01T00:00:00` });
  }

  return monthYearList;
};

const changeCheckboxValue = (checkValue: string) => {
  if (checkValue == 'byMonth') {
    tourDetail.value.isDepartureMonth = true;
    tourDetail.value.departureDay = null;
  } else {
    tourDetail.value.isDepartureMonth = false;
    tourDetail.value.departureMonth = '';
  }
  ruleValue.value.departureMonth = { requiredIfRef: requiredIf(tourDetail.value.isDepartureMonth) };
  ruleValue.value.departureDay = { requiredIfRef: requiredIf(!tourDetail.value.isDepartureMonth) };
  changeDateTour();
};

const onFilterGatewayTours = (event: any) => {
  const listGate = props.tourIndex > 0 ? getistGateways.value : props.listGateway;
  listGate.sort((first, second) => first[`gateName${capitalize(currentLanguage)}`]?.localeCompare(second[`gateName${capitalize(currentLanguage)}`]));
  if (!event.query.trim().length) {
    filterGateway.value = [...listGate.slice(0, 100)];
  } else {
    filterGateway.value = listGate.filter((gate: any) => {
      return (
        gate.gateCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gate[`cityName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gate[`region${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gate[`gateCountry${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gate[`gateName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const onFilterDestinationTours = (event: any) => {
  if (!event.query.trim().length) {
    filterDestination.value = [...getCityDestinationDetail.value.slice(0, 100)];
  } else {
    filterDestination.value = getCityDestinationDetail.value.filter((dest: any) => {
      return (
        dest.destCode?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        dest[`cityName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        dest[`region${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        dest[`destCountry${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        dest[`destName${capitalize(currentLanguage)}`]?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
  disableDate.value = false;
};

const selectedTourGateways = (event: any) => {
  if (props.tourIndex == 0) {
    clearDestination();
    emits('clearTours', props.tourIndex, 'destination');
    changeCheckboxValue('byMonth');
  }
  if (event.value?.gateCode) {
    tourManagementStore
      .getTourCombinationDestByGateCode(event.value?.gateCode)
      .then((res) => {
        listFullDest.value = res.data;
        getCityDestinationDetail.value = formatCountryFromCity(res.data);
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
      })
      .finally(() => {
        disableDestination.value = false;
      });
  }
};

const getDetailAddress = (dataProps: any, type: string) => {
  return dataProps[`cityName${capitalize(currentLanguage)}`] + ', ' + dataProps[`region${capitalize(currentLanguage)}`] + ', ' + dataProps[`${type}Country${capitalize(currentLanguage)}`];
};

const selectedTourDestination = (event: any) => {
  if (props.tourIndex == 0) {
    tourDetail.value.departureDay = null;
    tourDetail.value.departureMonth = '';
    tourDetail.value.isDepartureMonth = true;
    tourDetail.value.brochure = event?.value?.brochure;
    changeDateTour();
    changeCheckboxValue('byMonth');
  }
};

const clearGateway = () => {
  tourDetail.value.gateway = '';
  disableDestination.value = true;
  if (props.tourIndex == 0) {
    emits('clearTours', props.tourIndex, 'destination');
    clearDestination();
  }
};

const clearDestination = () => {
  tourDetail.value.destination = '';
  tourDetail.value.isDepartureMonth = true;
  tourDetail.value.departureDay = null;
  tourDetail.value.departureMonth = '';
  tourDetail.value.tourDurations = '';
  tourDetail.value.tourName = [];
  tourDetail.value.tours = [];
  getMonth.value = true;
  getDate.value = false;
  if (props.tourIndex == 0) {
    disableDate.value = false;
    emits('clearTours', props.tourIndex, 'destination');
  }
  changeCheckboxValue('byMonth');
};

const getTime = () => {
  if (props.firstOption.isDepartureMonth) {
    return props.firstOption.departureMonth;
  } else {
    return resetTimer(props.firstOption.departureDay);
  }
};

const groupToursByTourName = (tours: any) => {
  const groupedTours = {};

  tours.forEach((tour: any) => {
    const tourNameKey = `tourName${capitalize(currentLanguage)}`;
    const tourName = tour[tourNameKey];

    if (!groupedTours[tourName]) {
      groupedTours[tourName] = {
        ...tour,
        svId: new Set()
      };
    }

    groupedTours[tourName].svId.add(tour.svId);
  });

  return Object.values(groupedTours).map((tour: any) => ({
    ...tour,
    svId: Array.from(tour.svId).join(',')
  }));
};

const getTourDetailInfo = () => {
  listAllTours.value = [];
  const payload = {
    gateCode: props.firstOption.gateway.gateCode,
    gateId: props.firstOption.gateway.gateId,
    destCode: props.firstOption.destination.destCode,
    destId: props.firstOption.destination.destId,
    isDepartureMonth: props.firstOption.isDepartureMonth,
    departureDate: getTime(),
    returnDate: getTime(),
    durations: props.firstOption.tourDurations,
    groupType: props.advancedForm[0].groupTypeId
  };
  tourManagementStore
    .getTourDetail(payload)
    .then((res: any) => {
      if (res.data) {
        listTour.value = res.data;
        listAllTours.value = [...groupToursByTourName(listTour.value)];
        if (listTour.value.length) {
          listAllTours.value.unshift({
            [`tourName${capitalize(currentLanguage)}`]: t('multi_day_tour.all_tours'),
            svId: 'ALL',
            ttsId: 'ALL'
          });
        }
        if (props.tourIndex == 0) {
          tourManagementStore.updateListAllTours(listAllTours.value);
        }
      }
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    });
};

const changeDurations = () => {
  tourDetail.value.tourName = [];
  emits('clearTours', props.tourIndex, 'tourName');
};

const changeTourNames = () => {
  emits('clearTours', props.tourIndex, 'tourName');
};

const changeDateTour = () => {
  tourDetail.value.tourDurations = '';
  tourDetail.value.tourName = [];
  emits('clearTours', props.tourIndex, 'tourDurations');
};

const openModalAutoSearch = () => {
  tourSearchData.value = tourSearchData.value.flat();
  loadingForm.value = false;
  emits('openAutoSearch', false, tourSearchData.value);
};

const reSelectDate = (dateSelected: any) => {
  tourDetail.value.departureDay = new Date(dateSelected);
  changeCheckboxValue('byDay');
  tourDetail.value.isDepartureMonth = false;
};

const handleOpenModalDate = (listDate: any) => {
  const getListDate = [];
  listDate.map((date: any) => {
    getListDate.push({ date: date, price: null, isPackagePlus: false });
  });
  if (!filterDatesInRange(getListDate, minDateRangeValue.value, maxDateRangeValue.value).length) {
    return showModalErrorPackage(t('multi_day_tour.result_not_available'), t('multi_day_tour.departure_not_return'), t('multi_day_tour.please_update_query'));
  }
  const dialogRef = dialog.open(ModalSelectDate, {
    data: {
      availableDates: filterDatesInRange(getListDate, minDateRangeValue.value, maxDateRangeValue.value)
    },
    props: {
      modal: true,
      header: t('advance_quote.alternative_date'),
      style: {
        width: '560px'
      }
    },
    emits: {
      isSave: (dateSelected) => {
        dialogRef.close();
        reSelectDate(dateSelected);
      }
    }
  });
};

const getListSvId = (listTour: any) => {
  const filteredData = listTour.filter((item: any) => item.svId !== 'ALL');
  if (filteredData.length === 0) {
    return [];
  }
  return filteredData.map((item: any) => item.svId);
};

const getAllTourId = computed(() => {
  return tourDetail.value.tourName.svId == 'ALL' ? getListSvId(listAllTours.value) : [tourDetail.value.tourName.svId];
});

const getSearchTourAutoTotal = async (payload: any) => {
  showError.value = true;
  loadingForm.value = true;
  isGetTotal.value = false;
  const result = tourManagementStore
    .searchTourAuto(payload)
    .then((res: any) => {
      if (res.data.alternateDates?.length) {
        handleOpenModalDate(res.data?.alternateDates);
        totalItem.value = 0;
      } else {
        totalItem.value = res.data.totalElements;
      }
      isGetTotal.value = true;
    })
    .catch(() => {
      showModalErrorPackage(t('multi_day_tour.result_not_available'), t('multi_day_tour.departure_not_return'), t('multi_day_tour.please_update_query'));
    })
    .finally(() => {
      showError.value = false;
      loadingForm.value = false;
    });

  return result;
};

const getSearchTourAutoData = async (payload: any) => {
  const timesCall = Math.ceil(totalItem.value / limit);
  let payloadRecall = { ...payload };
  if (!isGetTotal.value || !totalItem.value) {
    return;
  }
  for (let i = 1; i <= timesCall; i++) {
    await tourManagementStore
      .searchTourAuto(payloadRecall)
      .then((res) => {
        totalItem.value = res.data.totalElements;
        tourSearchData.value.push(res.data.tours);
        tourSearchData.value = tourSearchData.value.flat();
        if (timesCall == i) {
          openModalAutoSearch();
        }
      })
      .catch((err) => {
        if (err?.response?.status == 500) {
          showModalErrorPackage(t('multi_day_tour.result_not_available'), t('multi_day_tour.departure_not_return'), t('multi_day_tour.please_update_query'));
        }
      });
    offset.value += limit;
    payloadRecall = {
      ...payload,
      offset: offset.value
    };
  }
};

const searchTourAuto = async () => {
  tourSearchData.value = [];
  await emits('handleSubmit');
  offset.value = 1;
  const payload = {
    departureCity: tourDetail.value.gateway.gateCode,
    destinationCity: tourDetail.value.destination.destId,
    month: tourDetail.value.departureMonth,
    departureDate: resetTimer(tourDetail.value.departureDay),
    duration: tourDetail.value.tourDurations,
    codeTours: getAllTourId.value,
    offset: offset.value
  };
  showError.value = true;
  v$.value.$validate();
  if (!v$.value.$error && props.isValidation) {
    await getSearchTourAutoTotal(payload);
    loadingForm.value = true;
    await getSearchTourAutoData(payload);
    loadingForm.value = false;
    tourManagementStore.updateTour(tourSearchData.value);
  }
};

const getListTourIds = (tourIds: any) => {
  if (tourIds.tourName.svId == 'ALL') {
    const listTourIds = cloneDeep(listAllTours.value);
    listTourIds.shift();
    return listTourIds.map((item: any) => item.svId).join(',');
  } else {
    return tourIds.tourName.svId;
  }
};

const getGatewayByDestCode = () => {
  const payload = {
    destCodes: props.firstOption.destination.destCode,
    tourIds: getListTourIds(props.firstOption)
  };
  if (props.firstOption.destination.destCode) {
    tourManagementStore
      .getTourCombinationGatewayByDestCode(payload)
      .then((res) => {
        if (res.data) {
          getistGateways.value = res.data;
        }
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
      });
  }
};

const showModalErrorPackage = (header: string, message: string, subMessage?: string) => {
  const modalRef = dialog.open(ValidateQuoteFormNotification, {
    data: {
      message: message,
      subMessage: subMessage
    },
    props: {
      header: header,
      modal: true
    },
    templates: {
      footer: () => {
        return [h('div', { class: 'text-center' }, h(Button, { autofocus: true, label: t('common.buttons.close'), onClick: () => modalRef.close() }))];
      }
    }
  });
};

const handleDateLimitationTour = (advancedFormTour: any) => {
  if (advancedFormTour) {
    minDateRangeValue.value = isWorksheetRequote.value ? new Date(DateTime.utc().plus({ days: 1 })) : getLimitDate(advancedFormTour, limitCalendar.value, 'startDateConfig');
    maxDateRangeValue.value = isWorksheetRequote.value ? undefined : getLimitDate(advancedFormTour, limitCalendar.value, 'endDateConfig');
  }
};

watch(
  () => tourDetail,
  (newValue) => {
    if (!newValue.value.adults) {
      newValue.value.adults = 0;
    }
    if (!newValue.value.children) {
      newValue.value.children = 0;
    }
    if (!newValue.value.numOfRooms) {
      newValue.value.numOfRooms = 0;
    }
  },
  { deep: true }
);

watch(
  () => isDisabledTourName,
  (newValue) => {
    if (newValue.value || (props.firstOption.tourDurations && (props.firstOption.departureMonth || props.firstOption.departureDay))) {
      getTourDetailInfo();
    }
  },
  { deep: true }
);

watch(
  () => tourDetail.value.tourDurations,
  (newValue) => {
    if (newValue) {
      getTourDetailInfo();
    }
  },
  { deep: true }
);

watch(
  () => tourDetail.value.isDepartureMonth,
  (newValue) => {
    getMonth.value = newValue;
    getDate.value = !newValue;
    ruleValue.value.departureMonth = { requiredIfRef: requiredIf(newValue) };
    ruleValue.value.departureDay = { requiredIfRef: requiredIf(!newValue) };
  },

  { immediate: true }
);

watch(
  () => listDestination.value,
  (newValue) => {
    if (newValue?.length) {
      getCityDestinationDetail.value = newValue;
    }
  },

  { immediate: true }
);

watch(
  () => props.advancedForm,
  (newValue) => {
    if (newValue) {
      tourDetail.value.departureDay = null;
      handleDateLimitationTour(newValue[0].groupTypeId);
    }
  },
  { deep: true }
);

watch(
  () => props.calendarData,
  (newValue) => {
    if (newValue) {
      limitCalendar.value = getQuoteDepartureData(newValue);
    }
  },
  { deep: true }
);

onMounted(() => {
  if (props.tourIndex > 0) {
    getGatewayByDestCode();
  }
  if (isRequote.value) {
    disableDestination.value = false;
    disableDate.value = false;
  }
});
</script>
<template>
  <div class="tab-input">
    <div class="grid">
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="gateway" class="long-text" v-tooltip.top="$t('advance_quote.form.gateway')">{{ $t('advance_quote.form.gateway') }}</label>
            <div class="flex align-items-center gateway-class">
              <AutoComplete
                v-model="tourDetail.gateway"
                :suggestions="filterGateway"
                @complete="onFilterGatewayTours($event)"
                :placeholder="$t('advance_quote.form.gateway')"
                :optionLabel="`gateName${capitalize(currentLanguage)}`"
                completeOnFocus
                @change="selectedTourGateways($event)"
                :class="showError && v$.gateway.$error ? 'p-invalid' : ''"
                class="w-full"
                id="gateway"
                forceSelection
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ slotProps.option[`gateName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.gateCode }})</div>
                      <div class="text-xs">{{ getDetailAddress(slotProps.option, 'gate') }}</div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
              <div>
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer times-circle-gateway-class" @click="clearGateway()"></i>
              </div>
            </div>
            <span class="text-red-500" v-if="showError && v$.gateway.$error"> {{ v$.gateway.required.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid>
          <div class="field">
            <label for="destination" class="long-text" v-tooltip.top="$t('advance_quote.form.destination')">{{ $t('advance_quote.form.destination') }}</label>
            <div class="flex align-items-center destination destination-class">
              <AutoComplete
                class="w-full"
                v-model="tourDetail.destination"
                :suggestions="filterDestination"
                @complete="onFilterDestinationTours($event)"
                optionGroupChildren="items"
                completeOnFocus
                :disabled="disableDestination || tourIndex !== 0"
                :placeholder="$t('advance_quote.form.destination')"
                :optionLabel="`destName${capitalize(currentLanguage)}`"
                :class="showError && v$.destination.$error ? 'p-invalid' : ''"
                @change="selectedTourDestination($event)"
                forceSelection
              >
                <template #option="slotProps">
                  <div v-if="slotProps.option.destCountryParent" class="font-bold h-full p-0">
                    {{ slotProps.option.destCountryParent }}
                  </div>
                  <div v-else class="ml-3">
                    <div>{{ slotProps.option[`destName${capitalize(currentLanguage)}`] }} ({{ slotProps.option.destCode }})</div>
                    <div class="text-xs">{{ getDetailAddress(slotProps.option, 'dest') }}</div>
                  </div>
                </template>
              </AutoComplete>
              <div v-if="tourIndex === 0">
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer times-circle-destination-class" @click="clearDestination()"></i>
              </div>
            </div>
            <span class="text-red-500" v-if="showError && v$.destination.$error"> {{ v$.destination.required.$message }} </span>
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid">
      <div class="col-12 lg:col-3 md:col-6">
        <Fluid>
          <div class="field departure-month-class">
            <div class="mb-2">
              <Checkbox inputId="byMonth" :binary="true" :disabled="disableDate || tourIndex !== 0" :modelValue="getMonth" @update:modelValue="changeCheckboxValue('byMonth')" />
              <label :disabled="disableDate" class="ml-1 cursor-pointer" :for="tourIndex !== 0 ? '' : 'byMonth'">{{ $t('advance_quote.form.by_departure_month') }}</label>
            </div>
            <Select
              v-model="tourDetail.departureMonth"
              :class="showError && v$.departureMonth.$error ? 'p-invalid' : ''"
              :options="getMonthYearList()"
              optionLabel="label"
              optionValue="value"
              placeholder="MMM-YYYY"
              id="list-departure-months"
              @change="changeDateTour()"
              :disabled="disableDate || !getMonth || tourIndex !== 0"
            />
            <span class="text-red-500" v-if="showError && v$.departureMonth.$error">{{ $t('validate.required') }}</span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 lg:col-3 md:col-6">
        <Fluid>
          <div class="field flex-col">
            <div class="mb-2">
              <Checkbox :modelValue="getDate" :binary="true" :disabled="disableDate || tourIndex !== 0" inputId="byDay" @update:modelValue="changeCheckboxValue('byDay')" />
              <label class="ml-1 cursor-pointer" :disabled="disableDate" :for="tourIndex !== 0 ? '' : 'byDay'">{{ $t('advance_quote.form.by_departure_date') }}</label>
            </div>
            <DatePicker
              :disabled="!getDate || tourIndex !== 0"
              :class="showError && v$.departureDay.$error ? 'p-invalid' : ''"
              id="date"
              dateFormat="dd-M-yy"
              v-model="tourDetail.departureDay"
              autocomplete="off"
              :placeholder="$t('common.date_format')"
              @update:modelValue="changeDateTour()"
              :minDate="minDateRangeValue"
              :maxDate="maxDateRangeValue"
            >
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
            <span class="text-red-500" v-if="showError && v$.departureDay.$error">{{ $t('validate.required') }}</span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid class="mt-5px">
          <div id="tour-durations-id" class="field tour-durations-class">
            <label for="gateway" class="long-text" v-tooltip.top="$t('advance_quote.form.duration')">{{ $t('advance_quote.form.duration') }}</label>
            <Select
              v-model="tourDetail.tourDurations"
              :disabled="!isDisabledDuration || props.tourIndex !== 0"
              :class="showError && v$.tourDurations.$error ? 'p-invalid' : ''"
              :options="listDuration"
              optionLabel="range"
              optionValue="value"
              @change="changeDurations()"
              :placeholder="$t('advance_quote.form.duration')"
            />
            <span class="text-red-500" v-if="showError && v$.tourDurations.$error">{{ v$.tourDurations.required.$message }}</span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 lg:col-2 md:col-4">
        <Fluid>
          <div class="field number-of-rooms-class">
            <label for="number_room" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.number_of_room')">{{ $t('advance_quote.form.nb_of_room') }}</label>
            <InputNumber :min="0" :max="999" id="number_room" :class="showError && v$.numOfRooms.$error ? 'p-invalid' : ''" type="text" v-model="tourDetail.numOfRooms" />
            <span class="text-red-500" v-if="showError && v$.numOfRooms.$error">{{ v$.numOfRooms.minValue.$message }}</span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 lg:col-2 md:col-4">
        <Fluid>
          <div class="field adults-class">
            <label for="adts" class="head-size" v-tooltip.top="$t('advance_quote.form.adult')">{{ $t('advance_quote.form.adult') }}</label>
            <InputNumber :min="0" :max="999" id="adts" :class="showError && v$.adults.$error ? 'p-invalid' : ''" type="text" v-model="tourDetail.adults" />
            <span class="text-red-500" v-if="showError && v$.adults.$error">{{ v$.adults.minValue.$message }} </span>
          </div>
        </Fluid>
      </div>
      <div class="col-12 lg:col-2 md:col-4">
        <Fluid>
          <div class="field child-class">
            <label for="chd" class="head-size long-text" v-tooltip.top="$t('advance_quote.form.children')">{{ $t('advance_quote.form.children_short') }}</label>
            <InputNumber :min="0" :max="999" id="chd" type="text" v-model="tourDetail.children" />
          </div>
        </Fluid>
      </div>
      <div class="col-12 md:col-6">
        <Fluid class="mt-n2">
          <div class="field tour-name-class">
            <label for="gateway" class="long-text" v-tooltip.top="$t('advance_quote.form.duration')">{{ $t('advance_quote.form.tour_names') }}</label>
            <Select
              :disabled="!isDisabledTourName || tourIndex !== 0"
              v-model="tourDetail.tourName"
              :class="showError && v$.tourName.$error ? 'p-invalid' : ''"
              :options="listAllTours"
              :optionLabel="`tourName${capitalize(currentLanguage)}`"
              :optionValue="(tour) => tour"
              @change="changeTourNames()"
              :placeholder="$t('advance_quote.form.tour_names')"
            ></Select>
            <span class="text-red-500" v-if="showError && v$.tourName.$error">{{ v$.tourName.required.$message }} </span>
          </div>
        </Fluid>
      </div>
    </div>
    <div class="grid flex justify-content-end" v-if="!displayTour && tourIndex == 0">
      <Button :label="$t('advance_quote.form.search_available_tours')" id="btn-search" class="button-submit mt-2 bg-primary uppercase search-tour-auto-class" @click="searchTourAuto()"></Button>
    </div>
  </div>
</template>
